import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Flex,
  Input,
  Radio,
  Select,
  Typography,
} from 'antd';
import type { AgentClient } from '../agentClient';
import { AgentApiError, toSafeMessage } from '../agentClient';
import {
  draftAllowed,
  introChoices,
  outcomeMessage,
  previewLines,
  previewStale,
  sendDisabled,
} from '../mimirSendView';
import type {
  MimirChannel,
  MimirChannels,
  MimirOutcome,
  MimirPreview,
  MimirSendInput,
} from '../types';

const MAX_TEXT = 4000;

const channelLabel = (c: MimirChannel): string =>
  `#${c.name}${c.mode ? ` [${c.mode}]` : ''}`;

/** Mimir's card: send its intro or your own message to a Slack channel (preview first; Send is the approval). */
const MimirSendSection = ({ client }: { client: AgentClient }) => {
  const [channels, setChannels] = useState<MimirChannels | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [channelId, setChannelId] = useState<string | undefined>();
  const [message, setMessage] = useState<'intro' | 'own'>('intro');
  const [intro, setIntro] = useState<'all' | 'mention' | undefined>();
  const [draft, setDraft] = useState(false);
  const [text, setText] = useState('');
  const [threadLink, setThreadLink] = useState('');
  const [preview, setPreview] = useState<MimirPreview | null>(null);
  const [previewed, setPreviewed] = useState<MimirSendInput | null>(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [refusal, setRefusal] = useState<MimirOutcome | null>(null);
  const [outcome, setOutcome] = useState<MimirOutcome | null>(null);
  // A preview or send that failed outright (no outcome from the service).
  const [actionError, setActionError] = useState<string | null>(null);

  const loadChannels = (signal?: AbortSignal) => {
    setLoadError(null);
    client
      .listMimirChannels(signal)
      .then((c) => {
        if (!signal?.aborted) setChannels(c);
      })
      .catch((e) => {
        if (!signal?.aborted) setLoadError(toSafeMessage(e));
      });
  };

  useEffect(() => {
    const ctl = new AbortController();
    loadChannels(ctl.signal);
    return () => ctl.abort();
  }, [client]);

  const channel = channels?.channels.find((c) => c.id === channelId);
  const choices = channel ? introChoices(channel) : [];
  const effectiveIntro =
    message === 'intro' && channel
      ? intro && choices.includes(intro)
        ? intro
        : choices[0]
      : undefined;
  const canDraft = !!channel && draftAllowed(channel);

  const link = threadLink.trim();
  const current: MimirSendInput | null = channel
    ? message === 'intro'
      ? {
          channel: channel.id,
          message: 'intro',
          intro: effectiveIntro,
          draft: draft && canDraft,
          ...(link ? { threadLink: link } : {}),
        }
      : {
          channel: channel.id,
          message: 'own',
          text,
          ...(link ? { threadLink: link } : {}),
        }
    : null;

  const clearResults = () => {
    setRefusal(null);
    setOutcome(null);
    setActionError(null);
  };

  const doPreview = async () => {
    if (!current) return;
    clearResults();
    setPreview(null);
    setPreviewed(null);
    setBusy(true);
    try {
      const result = await client.previewMimirSend(current);
      if ('sha256' in result) {
        setPreview(result);
        setPreviewed(current);
      } else {
        setRefusal(result);
      }
    } catch (e) {
      setActionError(toSafeMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const doSend = async () => {
    if (!current || !preview) return;
    clearResults();
    setSending(true);
    try {
      setOutcome(
        await client.sendMimirSend({ ...current, sha256: preview.sha256 }),
      );
    } catch (e) {
      // A sign-in refusal means nothing was posted; any other failure leaves the outcome unknown.
      setActionError(
        e instanceof AgentApiError ? e.message : outcomeMessage('unknown', []),
      );
    } finally {
      setSending(false);
    }
  };

  const previewReady =
    !!current &&
    !busy &&
    (message === 'intro' ? !!effectiveIntro : text.trim().length > 0);
  const result = outcome ?? refusal;
  const stale = !!current && previewStale({ preview, previewed, current });

  return (
    <div>
      <Typography.Text strong>Send to Slack</Typography.Text>
      <Flex vertical gap={8} style={{ marginTop: 4 }}>
        {loadError && (
          <Alert
            type="error"
            showIcon
            message={loadError}
            action={
              <Button size="small" onClick={() => loadChannels()}>
                Refresh
              </Button>
            }
          />
        )}
        <Flex gap={8} align="center" wrap>
          <Select
            style={{ minWidth: 240 }}
            placeholder="Channel"
            aria-label="Channel"
            value={channelId}
            options={(channels?.channels ?? []).map((c) => ({
              value: c.id,
              label: channelLabel(c),
            }))}
            loading={!channels && !loadError}
            onChange={(v) => {
              setChannelId(v);
              setDraft(false);
              setPreview(null);
              setPreviewed(null);
              clearResults();
            }}
          />
          <Button size="small" onClick={() => loadChannels()}>
            Refresh list
          </Button>
        </Flex>
        <Radio.Group
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        >
          <Radio value="intro">Intro</Radio>
          <Radio value="own">Your own message</Radio>
        </Radio.Group>
        {message === 'intro' && channel && (
          <Flex gap={12} align="center" wrap>
            <Select
              style={{ minWidth: 200 }}
              aria-label="Intro"
              value={effectiveIntro}
              options={choices.map((c) => ({
                value: c,
                label:
                  c === 'all' ? 'Intro: every report' : 'Intro: when tagged',
              }))}
              onChange={setIntro}
            />
            <Checkbox
              checked={draft && canDraft}
              disabled={!canDraft}
              onChange={(e) => setDraft(e.target.checked)}
            >
              Send my draft
            </Checkbox>
          </Flex>
        )}
        {message === 'own' && (
          <Input.TextArea
            value={text}
            maxLength={MAX_TEXT}
            showCount
            autoSize={{ minRows: 3, maxRows: 10 }}
            aria-label="Your message"
            placeholder="What should Mimir post?"
            onChange={(e) => setText(e.target.value)}
          />
        )}
        {channel && (
          <Input
            value={threadLink}
            maxLength={300}
            aria-label="Reply in thread (message link)"
            placeholder="Reply in thread (message link), optional"
            onChange={(e) => setThreadLink(e.target.value)}
          />
        )}
        <Flex gap={8}>
          <Button disabled={!previewReady} loading={busy} onClick={doPreview}>
            Preview
          </Button>
          <Button
            type="primary"
            loading={sending}
            disabled={
              !current || sendDisabled({ preview, previewed, current, sending })
            }
            onClick={doSend}
          >
            Send
          </Button>
        </Flex>
        {result && (
          <Alert
            type={result.ok ? 'success' : 'error'}
            showIcon
            message={
              <>
                {outcomeMessage(result.code, result.found)}
                {result.ok && result.permalink && (
                  <>
                    {' '}
                    <a
                      href={result.permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open in Slack
                    </a>
                  </>
                )}
              </>
            }
          />
        )}
        {actionError && <Alert type="error" showIcon message={actionError} />}
        {stale && (
          <Typography.Text type="secondary">
            Inputs changed; preview again.
          </Typography.Text>
        )}
        {preview && !stale && channels && (
          <div
            style={{
              borderLeft: '3px solid #d9d9d9',
              paddingLeft: 8,
            }}
          >
            {previewLines(preview, channels.botUserId).map((line, i) => (
              <div key={i} style={{ whiteSpace: 'pre-wrap', minHeight: '1em' }}>
                <Typography.Text type={i === 0 ? undefined : 'secondary'}>
                  {line}
                </Typography.Text>
              </div>
            ))}
          </div>
        )}
        {preview && !stale && preview.found.length > 0 && (
          <Alert
            type="error"
            showIcon
            message={outcomeMessage('client_data', preview.found)}
          />
        )}
      </Flex>
    </div>
  );
};

export default MimirSendSection;
